import { SEOHead } from "@/components/SEOHead";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, CreditCard, Fuel, Shield, Smartphone, MapPin, DollarSign, ChevronLeft, ChevronRight } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { Link } from "react-router-dom";
import { useState } from "react";

const testimonials = [
  {
    company: "KGZ Trucking, IL",
    quote: "\"These fuel cards have been a game-changer! We save on every gallon and can track spending in real-time. Perfect for managing our fleet efficiently.\"",
  },
  {
    company: "Sunrise Logistics, TX",
    quote: "\"AITIM Fuel Cards saved us thousands every month. The WEX app makes it easy to find the cheapest stations on every route.\"",
  },
  {
    company: "BlueStar Transport, OH",
    quote: "\"The factoring bundle with the fuel card is unbeatable. We get paid fast and save at the pump — two problems solved at once.\"",
  },
];

const features = [
  {
    icon: DollarSign,
    title: "Savings and Credit",
    desc: "Save an average of 70¢ per gallon and receive up to a $6,000 line of credit weekly.",
  },
  {
    icon: Fuel,
    title: "Expanding Fuel Network",
    desc: "Discounts at 14,000+ fuel stations nationwide and counting, including TA Express®, 7FLEET, TA® and more.",
  },
  {
    icon: Shield,
    title: "Fraud Prevention",
    desc: "Easy-to-use card lock feature provides added security from fuel card fraud.",
  },
  {
    icon: Smartphone,
    title: "WEX Mobile App",
    desc: "Use our mobile app to find the lowest fuel prices along your route.",
  },
  {
    icon: CreditCard,
    title: "Universal Acceptance",
    desc: "Get point-of-sale fuel discounts at more than 10.3 million Mastercard locations.",
  },
  {
    icon: ArrowRight,
    title: "Factoring Bundle",
    desc: "Extend your savings beyond fuel by bundling your fuel card with AITIM Factoring.",
  },
];

const fuelCardSchema = {
  "@context": "https://schema.org",
  "@type": "Service",
  "serviceType": "Fuel Card Program",
  "provider": {
    "@type": "LocalBusiness",
    "name": "AITIM Group"
  },
  "areaServed": "US",
  "description": "Big savings at more than 14,000 fuel stations and credit lines of up to $6,000 per truck, per week."
};

const FuelCards = () => {
  const { t } = useLanguage();
  const [activeTestimonial, setActiveTestimonial] = useState(1);

  return (
    <div className="min-h-screen bg-background font-sans">
      <SEOHead 
        title="Average Savings of 70¢ Per Gallon | Fuel Card Program"
        description="Big savings at more than 14,000 fuel stations and credit lines of up to $6,000 per truck, per week."
        canonical="/fuel-cards"
        schemaJson={fuelCardSchema}
      />
      <Navbar />

      <main>
        {/* Hero Section */}
        <section className="bg-[#000000] pt-0 overflow-hidden">
          <div className="flex flex-col lg:flex-row min-h-[640px]">
            {/* Left — Text */}
            <div className="flex-1 flex flex-col justify-center px-6 md:px-16 lg:px-24 pt-32 pb-10 lg:pt-48 lg:pb-20">
              <div className="text-primary font-bold tracking-widest uppercase text-sm mb-4">
                FUEL CARD PROGRAM
              </div>
              <h1 className="text-3xl md:text-5xl lg:text-6xl font-black text-white mb-6 leading-tight">
                Average Savings of<br />
                <span className="text-primary">70¢ Per Gallon</span>
              </h1>
              <p className="text-base md:text-lg text-white/70 mb-10 max-w-lg leading-relaxed">
                Big savings at more than 14,000 fuel stations and credit lines of up to $6,000 per truck, per week.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/free-consultation">
                  <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-14 text-lg font-semibold w-full sm:w-auto">
                    FILE NOW <ArrowRight className="ml-2 w-5 h-5" />
                  </Button>
                </Link>
              </div>
            </div>

            {/* Right — Card Image */}
            <div className="flex-1 flex items-center justify-center bg-[#000000] px-6 py-10 lg:p-16">
              <img
                src="https://vibe.filesafe.space/1777392978847194987/attachments/0ed7bb7b-de71-40cc-96df-ec18ee4e5966.png"
                alt="AITIM Capital Fuel Card"
                className="w-full max-w-[380px] md:max-w-[480px] h-auto object-contain drop-shadow-2xl"
              />
            </div>
          </div>
        </section>

        {/* About Fuel Network — Features Grid */}
        <section className="py-24 bg-white">
          <div className="container mx-auto px-4 max-w-6xl">
            <div className="text-center mb-16">
              <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">ABOUT OUR NETWORK</div>
              <h2 className="text-3xl md:text-5xl font-black text-navy-950">About Our Fuel Network</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {features.map((feature, i) => (
                <div
                  key={i}
                  className="group bg-slate-50 rounded-2xl p-8 border border-slate-100 hover:-translate-y-2 hover:shadow-2xl hover:shadow-primary/10 hover:border-primary/20 transition-all duration-500"
                >
                  <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors duration-300">
                    <feature.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
                  </div>
                  <h3 className="text-xl font-bold text-navy-950 mb-3">{feature.title}</h3>
                  <p className="text-slate-500 leading-relaxed text-sm">{feature.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Quote Banner */}
        <section className="bg-navy-950 py-20">
          <div className="container mx-auto px-4 text-center">
            <p className="text-2xl md:text-3xl font-bold text-white mb-10 italic">
              "Fuel Your Savings, Drive Your Success"
            </p>
            <Link to="/free-consultation">
              <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-10 h-14 text-lg font-semibold">
                FILE NOW <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>

        {/* Card Info — Image + Text */}
        <section className="py-0 bg-white overflow-hidden">
          <div className="flex flex-col lg:flex-row min-h-[460px]">
            {/* Left Image */}
            <div className="lg:w-1/2 relative overflow-hidden min-h-[300px] lg:min-h-auto">
              <img
                src="https://static.wixstatic.com/media/5c8a3f_a6b593b4380a4d9db88c23acc5d0391c~mv2.png/v1/fill/w_958,h_471,al_c,q_90,usm_0.66_1.00_0.01,enc_avif,quality_auto/5c8a3f_a6b593b4380a4d9db88c23acc5d0391c~mv2.png"
                alt="AITIM Fuel Card in hand"
                className="w-full h-full object-cover object-center"
              />
            </div>
            {/* Right Text */}
            <div className="lg:w-1/2 flex flex-col justify-center px-8 md:px-16 lg:px-20 py-16">
              <div className="text-primary font-bold tracking-widest uppercase text-xs mb-4">BUSINESS SOLUTION</div>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-navy-950 mb-6 leading-tight">
                A Card to Match<br />Your Business Needs
              </h2>
              <p className="text-slate-500 leading-relaxed text-base md:text-lg mb-8">
                A fuel card helps businesses manage fuel expenses with ease, offering real-time tracking, spending controls, and discounts that can add up significantly over time. With nationwide acceptance and simplified reporting, fuel cards provide essential savings and streamlined management for fleets and frequent travelers alike.
              </p>
              <div>
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-white rounded-sm px-8 h-12 font-semibold">
                  Get Your Card <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section className="bg-navy-950 py-24">
          <div className="container mx-auto px-4 max-w-3xl text-center relative">
            <div className="text-primary font-bold tracking-widest uppercase text-xs mb-6">TESTIMONIALS</div>

            <div className="relative min-h-[160px] flex flex-col items-center justify-center">
              <p className="text-white font-semibold text-sm mb-3 tracking-wide">
                {testimonials[activeTestimonial].company}
              </p>
              <div className="w-8 h-0.5 bg-primary mx-auto mb-6"></div>
              <p className="text-white/80 text-lg md:text-xl italic leading-relaxed">
                {testimonials[activeTestimonial].quote}
              </p>
            </div>

            {/* Dots + Arrows */}
            <div className="flex items-center justify-center gap-6 mt-10">
              <button
                onClick={() => setActiveTestimonial((prev) => (prev - 1 + testimonials.length) % testimonials.length)}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors"
              >
                <ChevronLeft className="w-5 h-5 text-white" />
              </button>
              <div className="flex gap-2">
                {testimonials.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveTestimonial(i)}
                    className={`w-2.5 h-2.5 rounded-full transition-all duration-300 ${i === activeTestimonial ? "bg-primary scale-125" : "bg-white/30 hover:bg-white/60"}`}
                  />
                ))}
              </div>
              <button
                onClick={() => setActiveTestimonial((prev) => (prev + 1) % testimonials.length)}
                className="w-10 h-10 rounded-full border border-white/20 flex items-center justify-center hover:bg-primary hover:border-primary transition-colors"
              >
                <ChevronRight className="w-5 h-5 text-white" />
              </button>
            </div>
          </div>
        </section>

        {/* CTA Banner */}
        <section className="bg-primary py-16">
          <div className="container mx-auto px-4 text-center">
            <h2 className="text-2xl md:text-4xl font-black text-white mb-4">Ready to Start Saving at the Pump?</h2>
            <p className="text-white/80 text-lg mb-8 max-w-xl mx-auto">
              Join thousands of trucking companies that trust AITIM's fuel card program to cut costs and simplify fleet management.
            </p>
            <Link to="/free-consultation">
              <Button size="lg" className="bg-white text-primary hover:bg-white/90 rounded-sm px-10 h-14 text-lg font-bold">
                Apply Now <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default FuelCards;
